import React, { useState, useEffect, useCallback, useMemo } from "react";
import CardTable from "components/CardTable/CardTable";
import { useOpenComponent } from "hooks/useOpenComponent";
import { Autocomplete, TextField } from "@mui/material";

import { useDispatch, useSelector } from "react-redux";

import { columns } from "./columns";

import { Home } from "@mui/icons-material";
import { useSearch } from "hooks/useSearch";
import { ModalV2 } from "components/Modal/ModalV2";

import { CAPTURES_PHRASE } from "./config";

import { CapturesForm } from "./CapturesForm";
import { CaptureTableInputs } from "./CaptureTableInputs";
import { Modal } from "components/Modal/Modal";
import { useToggle } from "hooks/useToggle";
import { TableInputs } from "components/CardTable/TableInputs";
import {
  fetchCaptures,
  fetchCancelCapture,
  clearCaptures,
  fetchCancelMotives,
  handleCapturesInputsAction,
} from "redux/actions/buildings/captures/capturesActions";

const LOADINGS = [
  "fetchSyncCycle",
  "fetchCloseCycle",
  "fetchCaptures",
  "fetchRouteCycles",
  "fetchEditCapture",
  "fetchCancelCapture",
  "fetchCancelMotives"
];

export const CapturesPage = () => {
  const dispatch = useDispatch();
  const { isOpen, handleIsOpen } = useOpenComponent();

  const {
    setSnackbar,
    clearSearch,
    buildCompLoadings
  } = useSearch();
  const { motives } = useSelector(
    state => state.captures
  );
  const { isLoadingComp } = useSelector(state => state.search);
  const [state, setState] = useState({
    idCapture: "",
    idRouteCycle: "",
    imageUrl: "",
    idMotive: "",
    capture: null
  });
  const [cancelModal, setCancelModal] = useToggle();
  const [imageModal, setImageModal] = useToggle();

  const { captures, routeCycleSelected, isAddMode, deleteMode } = useSelector(state => state.captures);

  const handleOpenCancel = async (idCapture, idRouteCycle) => {
    dispatch(fetchCancelMotives());
    setCancelModal();
    setState(prevState => ({ ...prevState, idCapture, idRouteCycle }));
  };

  const handleOpenImage = imageUrl => {
    setImageModal();
    setState(prevState => ({ ...prevState, imageUrl }));
  };

  const capturesMemo = useMemo(
    () =>
      captures.map(capture => {
        return {
          ...capture,
          idRouteCycle: routeCycleSelected?.id,
          apartment: capture.apartment.description,
          customer: capture.customer.name,
          address: capture.apartment.address,
          modal: handleIsOpen,
          cancel: handleOpenCancel,
          imageModal: handleOpenImage
        };
      }),
    [captures, routeCycleSelected, state, cancelModal]
  );

  const handleSubmit = useCallback(() => {
    if (!routeCycleSelected?.id) {
      setSnackbar(CAPTURES_PHRASE.page.table.header.snackBar);
      return;
    }
    dispatch(fetchCaptures(routeCycleSelected.id));
  }, [routeCycleSelected, setSnackbar]);

  const handleCancel = useCallback(() => {
    dispatch(
      fetchCancelCapture(state.idRouteCycle, state.idCapture, state.idMotive, () =>
        setCancelModal()
      )
    );
  }, [state]);

  const handleChangeMotive = (e, v) => {
    setState(prevState => ({ ...prevState, idMotive: v.id }));
  };

  const handleShowImage = useCallback(() => {
    if (state.imageUrl) {
      window.open(state.imageUrl, "_blank", "noopener,noreferrer");
    } else {
      setSnackbar(CAPTURES_PHRASE.search.messages.noImage);
    }
    setImageModal();
  }, [state]);

  const submitMemoized = useCallback((e) => {
    e.preventDefault();
    handleSubmit();
  }, [handleSubmit]);

  const handleClose = () => {
    handleIsOpen();
  };

  useEffect(() => {
    clearSearch();
    dispatch(clearCaptures());
  }, [clearSearch]);

  useEffect(() => {
    buildCompLoadings(LOADINGS);
  }, []);

  return (
    <React.Fragment>
      <CardTable
        data={capturesMemo}
        columns={columns}
        loading={isLoadingComp?.fetchCaptures}
        title={CAPTURES_PHRASE.page.table.title}
        icon={<Home />}
        hasHeader
        tableContent={
          <>
            <TableInputs
              reducer="captures"
              stations
              dispatchAction={handleCapturesInputsAction}
              submitAction={submitMemoized}
            >
              <CaptureTableInputs />
            </TableInputs>
          </>
        }
        blockBtn
      />
      <ModalV2 
      open={isOpen} 
      onClose={handleClose} 
      toRight
      title={isAddMode
                ? CAPTURES_PHRASE.modal.titles.add
                : deleteMode
                  ? CAPTURES_PHRASE.modal.titles.delete
                  : CAPTURES_PHRASE.modal.titles.edit}
      >
        <CapturesForm handleClose={handleClose} />
      </ModalV2>
      <Modal
        isOpen={cancelModal}
        handleClose={setCancelModal}
        textButton1={CAPTURES_PHRASE.modal.actions.cancel}
        textButton2={CAPTURES_PHRASE.modal.actions.close}
        actionFunction1={setCancelModal}
        actionFunction2={handleCancel}
        textContent={CAPTURES_PHRASE.modal.content.cancel}
        isDisabled={isLoadingComp.fetchCancelCapture || !state.idMotive}
        children={
          <Autocomplete
            options={motives}
            disableClearable
            getOptionLabel={option => option.motive}
            renderInput={params => (
              <TextField
                {...params}
                name="cancelMotives"
                label={CAPTURES_PHRASE.modal.dropdown.label}
                variant="outlined"
                size="small"
              />
            )}
            fullWidth
            onChange={(e, v) => handleChangeMotive(e, v)}
            noOptionsText={
              CAPTURES_PHRASE.page.table.header.dropdown.noOptions
            }
          />
        }
      />
      <Modal
        isOpen={imageModal}
        handleClose={setImageModal}
        textButton1={CAPTURES_PHRASE.modal.actions.submit}
        textButton2={CAPTURES_PHRASE.modal.actions.close}
        actionFunction1={setImageModal}
        actionFunction2={handleShowImage}
        textContent={CAPTURES_PHRASE.modal.content.image}
        isDisabled={isLoadingComp.fetchCancelCapture}
      />
    </React.Fragment>
  );
};
